import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import { Typography, Tooltip, Button, IconButton, TextField, InputAdornment } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import { i18n } from '../../translate/i18n';
import matchesLaneContactSearch from '../../helpers/matchesLaneContactSearch';

/** Limite inicial de cards no DOM por coluna (evita travar com milhares de tickets). */
const KANBAN_INITIAL_VISIBLE_CARDS = 100;
const KANBAN_LOAD_MORE_STEP = 100;

const matchesLaneSearch = matchesLaneContactSearch;

const useStyles = makeStyles(theme => ({
  column: props => ({
    backgroundColor: props.color || '#ebecf0',
    borderRadius: 8,
    minWidth: 272,
    maxWidth: 272,
    height: '100%',
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    minHeight: 0,
  }),
  /** Nome da lane: linha própria no topo para não competir com badge/total (evita "Agendamen..."). */
  laneTitle: {
    fontWeight: 'bold',
    fontSize: '0.875rem',
    color: "#D6D6D6",
    flex: 1,
    minWidth: 0,
    lineHeight: 1.35,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  cardList: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    ...theme.scrollbarStyles,
  },
  loadMoreWrap: {
    padding: theme.spacing(0.5, 0),
    flexShrink: 0,
  },
  loadMoreHint: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    display: 'block',
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.35,
  },
  laneHeaderRoot: {
    marginBottom: theme.spacing(1),
  },
  laneTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  laneSearchToggle: {
    color: 'rgba(255, 255, 255, 0.85)',
    padding: theme.spacing(0.25),
    marginTop: 1,
    flexShrink: 0,
    '&:hover': {
      color: '#ffffff',
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
  },
  laneSearchField: {
    marginTop: theme.spacing(0.75),
    marginBottom: theme.spacing(0.25),
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      fontSize: '0.8125rem',
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 10px',
    },
  },
  /** Linha inferior: contagem + arrastar | total */
  laneToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    minWidth: 0,
  },
  laneToolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    flexShrink: 0,
  },
  totalValue: {
    fontSize: '0.8125rem',
    color: "#D6D6D6",
    fontWeight: 'bold',
    flexShrink: 0,
    textAlign: 'right',
    lineHeight: 1.2,
  },
  dragHandle: {
    cursor: 'grab',
    color: 'rgba(255, 255, 255, 0.7)',
    padding: theme.spacing(0.5),
    '&:hover': {
      color: 'rgba(255, 255, 255, 1)',
    },
  },
  columnContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  countBadge: {
    minWidth: 24,
    height: 24,
    padding: '0 7px',
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    // Fundo sempre claro: no dark mode text.primary é claro e sumia no balão
    color: theme.palette.getContrastText('#ffffff'),
    fontSize: '0.75rem',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(0.75),
    flexShrink: 0,
    boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
    lineHeight: 1,
  },
}));

const KanbanColumn = ({ id, title, tickets, color, index, updateTicket, onTicketClosed, onTicketTransferred, isAdmin }) => {
  const classes = useStyles({ color });

  const [visibleCount, setVisibleCount] = useState(KANBAN_INITIAL_VISIBLE_CARDS);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setVisibleCount(KANBAN_INITIAL_VISIBLE_CARDS);
  }, [id]);

  useEffect(() => {
    setVisibleCount(KANBAN_INITIAL_VISIBLE_CARDS);
  }, [searchQuery]);

  const filteredTickets = useMemo(
    () => tickets.filter(ticket => matchesLaneSearch(ticket, searchQuery)),
    [tickets, searchQuery]
  );

  /**
   * Quando tickets.length era 0 (carregando), o clamp antigo fazia visibleCount = 0.
   * Ao passar a haver tickets, Math.min(0, n) permanecia 0 — sumiam todos os cards.
   * Só ajustamos quando já há tickets; garantimos floor INITIAL e teto tickets.length.
   */
  useEffect(() => {
    if (filteredTickets.length === 0) return;
    setVisibleCount(prev =>
      Math.min(Math.max(prev, KANBAN_INITIAL_VISIBLE_CARDS), filteredTickets.length)
    );
  }, [filteredTickets.length]);

  const visibleTickets = useMemo(
    () => filteredTickets.slice(0, visibleCount),
    [filteredTickets, visibleCount]
  );

  const handleToggleSearch = useCallback(() => {
    setSearchOpen(prev => {
      if (prev) {
        setSearchQuery('');
      }
      return !prev;
    });
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleLoadMore = useCallback(() => {
    setVisibleCount(c => c + KANBAN_LOAD_MORE_STEP);
  }, []);

  const totalValue = filteredTickets.reduce((acc, ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return acc + opportunityValue;
  }, 0);

  const ticketCount = filteredTickets.length;
  const totalTicketCount = tickets.length;
  const isFiltering = searchQuery.trim().length > 0;

  return (
    <Draggable draggableId={id} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.column}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div className={classes.laneHeaderRoot}>
            <div className={classes.laneTitleRow}>
              <Tooltip title={i18n.t('kanban.laneSearchTooltip')}>
                <IconButton
                  size="small"
                  className={classes.laneSearchToggle}
                  onClick={handleToggleSearch}
                  aria-label={i18n.t('kanban.laneSearchAria')}
                  aria-pressed={searchOpen}
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography className={classes.laneTitle} component="div" variant="subtitle2">
                {title}
              </Typography>
            </div>
            {searchOpen && (
              <TextField
                className={classes.laneSearchField}
                variant="outlined"
                size="small"
                fullWidth
                autoFocus
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={i18n.t('kanban.laneSearchPlaceholder')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch} aria-label={i18n.t('kanban.laneSearchClearAria')}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}
            <div className={classes.laneToolbar}>
              <div className={classes.laneToolbarLeft}>
                <Tooltip
                  title={
                    isFiltering
                      ? i18n.t('kanban.laneTicketCountFilteredTooltip', {
                          filtered: ticketCount,
                          total: totalTicketCount,
                        })
                      : i18n.t('kanban.laneTicketCountTooltip', { count: ticketCount })
                  }
                >
                  <span
                    className={classes.countBadge}
                    aria-label={
                      isFiltering
                        ? i18n.t('kanban.laneTicketCountFilteredAria', {
                            filtered: ticketCount,
                            total: totalTicketCount,
                          })
                        : i18n.t('kanban.laneTicketCountAria', { count: ticketCount })
                    }
                  >
                    {isFiltering ? `${ticketCount}/${totalTicketCount}` : ticketCount}
                  </span>
                </Tooltip>
                {isAdmin && (
                  <div
                    {...provided.dragHandleProps}
                    className={classes.dragHandle}
                  >
                    <DragIndicatorIcon fontSize="small" />
                  </div>
                )}
              </div>
              <Typography className={classes.totalValue} component="span">
                Total: R$ {totalValue.toFixed(2)}
              </Typography>
            </div>
          </div>
          <Droppable droppableId={id} type="CARD">
            {(provided, snapshot) => (
              <div
                className={classes.cardList}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {visibleTickets.map((ticket, index) => (
                  <KanbanCard
                    key={ticket.id}
                    ticket={ticket}
                    index={index}
                    updateTicket={updateTicket}
                    onTicketClosed={onTicketClosed}
                    onTicketTransferred={onTicketTransferred}
                  />
                ))}
                {ticketCount > visibleTickets.length && (
                  <div className={classes.loadMoreWrap}>
                    <Typography className={classes.loadMoreHint} component="span">
                      {i18n.t('kanban.showingCardsPartial', {
                        visible: visibleTickets.length,
                        total: ticketCount,
                      })}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onClick={handleLoadMore}
                    >
                      {i18n.t('kanban.loadMoreCards')}
                    </Button>
                  </div>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

const areKanbanColumnPropsEqual = (prevProps, nextProps) => {
  if (
    prevProps.id !== nextProps.id ||
    prevProps.title !== nextProps.title ||
    prevProps.color !== nextProps.color ||
    prevProps.index !== nextProps.index ||
    prevProps.isAdmin !== nextProps.isAdmin ||
    prevProps.updateTicket !== nextProps.updateTicket ||
    prevProps.onTicketClosed !== nextProps.onTicketClosed ||
    prevProps.onTicketTransferred !== nextProps.onTicketTransferred
  ) {
    return false;
  }

  const prevTickets = prevProps.tickets || [];
  const nextTickets = nextProps.tickets || [];

  if (prevTickets.length !== nextTickets.length) return false;

  for (let i = 0; i < prevTickets.length; i += 1) {
    if (prevTickets[i] !== nextTickets[i]) {
      return false;
    }
  }

  return true;
};

export default memo(KanbanColumn, areKanbanColumnPropsEqual);
